import { useEffect, useState } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import Markdown from 'react-markdown'
import { useNavigate } from 'react-router-dom'

import { Button, Divider, Icon, Layout, Page, Text } from '@loadsmart/miranda-react'

import analyticsLibraryIllustration from 'assets/images/analytics-library-illustration.svg'
import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import { RequestType } from 'core/domain/RequestType'
import AppLayout from 'core/ui/components/AppLayout'
import Header from 'core/ui/components/Header'
import ZendeskDialog from 'core/ui/components/zendesk/TryNowZendeskDialog'
import { getPBIDemoReportsQueryOptions } from 'managed-analytics/use-cases/getPBIReports'

import { StyledContainer, StyledReportCard, StyledReportImage } from './AnalyticsLibraryPage.styles'

const TRACK_EVENT_EXPIRY_MS = 1 * 60 * 1000

const AnalyticsLibrary = () => {
  const demoReportsQuery = useSuspenseQuery(getPBIDemoReportsQueryOptions())

  const [zendeskDialogRequestType, setZendeskDialogRequestType] = useState<RequestType | null>(null)
  const [isZendeskDialogOpen, setIsZendeskDialogOpen] = useState(false)

  const { track } = useAnalytics()

  const navigate = useNavigate()

  useEffect(() => {
    const userAgent = window.navigator.userAgent
    const isMobile = /Mobi/i.test(userAgent)

    if (isMobile) {
      navigate('/mobile-not-supported')
    }
  }, [navigate])

  // This code is functionally equivalent to what was previously here
  // but we're now using react query to fetch the data
  // We're still tracking the event after the data is loaded
  useEffect(() => {
    if (!demoReportsQuery.data) return

    function shouldTrackEvent() {
      const lastTracked = localStorage.getItem('lastTrackedDemoEvent')
      if (!lastTracked) return true

      const now = Date.now()
      return now - parseInt(lastTracked, 10) > TRACK_EVENT_EXPIRY_MS
    }

    if (shouldTrackEvent()) {
      track(AnalyticsEvent.OpenDemo)
      localStorage.setItem('lastTrackedDemoEvent', Date.now().toString())
    }
  }, [demoReportsQuery.data, track])

  return (
    <AppLayout>
      <Header unauthCtaLabel="Sign in" unauthCtaHref="/" />
      <Page.Content>
        <Page.Body>
          <Layout.Stack gap="spacing-6" align="center">
            <StyledContainer
              backgroundColor="color-background-primary"
              borderColor="color-border"
              borderWidth="border-thin"
              paddingL="spacing-12"
              paddingR="spacing-6"
              paddingT="spacing-6"
              paddingB="spacing-6"
            >
              <Layout.Group align="center" gap="spacing-20">
                <Layout.Stack gap="spacing-6" style={{ flex: 1 }}>
                  <Text
                    variant="hero-sm-bold"
                    color="color-primary-100"
                    style={{ lineHeight: '125%' }}
                  >
                    Powerful, customizable BI reporting and data workflows at your fingertips.
                  </Text>
                  <Text variant="body-md" color="color-text-tertiary">
                    Access ready-made or fully customizable exportable reports built around
                    objective problem statements, actionable data, and specific to your unique
                    business rules. Leverage our professional services to automate data workflows
                    and centralize your data in one place. Manage multi-user access both inside and
                    outside your org. Partner with Loadsmart to develop a value-driven data strategy
                    where transportation becomes your competitive weapon.
                  </Text>
                  <Layout.Group>
                    <Button
                      variant="primary"
                      onClick={() => {
                        setZendeskDialogRequestType(RequestType.Question)
                        setIsZendeskDialogOpen(true)
                      }}
                    >
                      Talk to an expert
                    </Button>
                    <Button
                      variant="tertiary"
                      leading={<Icon name="arrow-sort-down" />}
                      onClick={() => {
                        document
                          .getElementById('report-list')
                          ?.scrollIntoView({ behavior: 'smooth' })
                      }}
                    >
                      Explore & preview custom reports ({demoReportsQuery.data.length})
                    </Button>
                  </Layout.Group>
                </Layout.Stack>
                <div>
                  <img src={analyticsLibraryIllustration} alt="" className="w-[750px]" />
                </div>
              </Layout.Group>
            </StyledContainer>

            <Layout.Stack id="report-list" gap="spacing-6" align="center">
              {demoReportsQuery.data.map(report => (
                <StyledReportCard
                  key={report.uuid}
                  backgroundColor="color-background-primary"
                  borderColor="color-border"
                  borderWidth="border-thin"
                  padding="none"
                >
                  <Layout.Group padding="spacing-6" gap="spacing-12">
                    <StyledReportImage src={report.thumbnailUrl} alt="" />
                    <Layout.Stack paddingY="spacing-6" gap="spacing-4" style={{ flex: 1 }}>
                      <Text variant="heading-md-bold" color="color-text-primary">
                        {report.reportName}
                      </Text>
                      <Text variant="body-md" color="color-text-secondary">
                        <Markdown>{report.description}</Markdown>
                      </Text>
                    </Layout.Stack>
                  </Layout.Group>
                  <Divider />
                  <Layout.Group padding="spacing-6" gap="spacing-2">
                    <Button
                      variant="secondary"
                      leading={<Icon name="send" />}
                      onClick={() => {
                        setZendeskDialogRequestType(RequestType.NewRequest)
                        setIsZendeskDialogOpen(true)
                      }}
                    >
                      Request report
                    </Button>
                    <Button
                      variant="tertiary"
                      leading={<Icon name="eye" />}
                      href={report.demoWithoutData ? undefined : `/demo-reports/${report.uuid}`}
                      disabled={report.demoWithoutData}
                      onClick={e => {
                        if (report.demoWithoutData) return

                        e.preventDefault()

                        navigate(`/demo-reports/${report.uuid}`)
                      }}
                    >
                      Preview
                    </Button>
                  </Layout.Group>
                </StyledReportCard>
              ))}
            </Layout.Stack>
          </Layout.Stack>
        </Page.Body>
      </Page.Content>

      {zendeskDialogRequestType && (
        <ZendeskDialog
          requestType={zendeskDialogRequestType}
          open={isZendeskDialogOpen}
          onClose={() => {
            setIsZendeskDialogOpen(false)
          }}
        />
      )}
    </AppLayout>
  )
}

export default AnalyticsLibrary
